import { useState } from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../services/firebase';
import { exchangeFirebaseTokenForJWT } from '../services/adminAuthService';

interface AdminLoginPageProps {
    onLoginSuccess: () => void;
}

const AdminLoginPage = ({ onLoginSuccess }: AdminLoginPageProps) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            // Sign in with Firebase Auth
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            // Get ID token to check custom claims
            const idTokenResult = await user.getIdTokenResult();
            const adminRole = idTokenResult.claims.adminRole;

            // Verify user has admin role
            if (!adminRole) {
                await auth.signOut();
                setError('Access denied. This account does not have admin privileges.');
                setIsLoading(false);
                return;
            }

            // Exchange email/password for admin JWT
            await exchangeFirebaseTokenForJWT(email, password);

            console.log('✅ Admin login success:', email, '- Role:', adminRole);
            onLoginSuccess();
        } catch (err: any) {
            console.error('Login error:', err);

            // Handle specific Firebase Auth errors
            if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password') {
                setError('Invalid email or password.');
            } else if (err.code === 'auth/too-many-requests') {
                setError('Too many failed login attempts. Please try again later.');
            } else if (err.code === 'auth/network-request-failed') {
                setError('Network error. Please check your connection.');
            } else {
                setError('Login failed. Please try again.');
            }

            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800 flex items-center justify-center p-4">
            <div className="max-w-md w-full">
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 backdrop-blur-sm rounded-2xl mb-4">
                        <span className="text-4xl">🛡️</span>
                    </div>
                    <h1 className="text-3xl font-bold text-white mb-2">
                        Admin Portal
                    </h1>
                    <p className="text-indigo-200">
                        Wellbeing Coach Administration
                    </p>
                </div>

                <div className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl p-8 border border-white/20">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label htmlFor="admin-email" className="block text-sm font-medium text-white mb-2">
                                Admin Email
                            </label>
                            <input
                                id="admin-email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition"
                                placeholder="admin@wellbeingcoach.com"
                                disabled={isLoading}
                            />
                        </div>

                        <div>
                            <label htmlFor="admin-password" className="block text-sm font-medium text-white mb-2">
                                Password
                            </label>
                            <input
                                id="admin-password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition"
                                placeholder="••••••••"
                                disabled={isLoading}
                            />
                        </div>

                        {error && (
                            <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4">
                                <p className="text-red-100 text-sm">{error}</p>
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={isLoading}
                            className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold py-3 px-6 rounded-lg hover:from-indigo-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2 focus:ring-offset-purple-900 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isLoading ? 'Authenticating...' : 'Sign In'}
                        </button>
                    </form>

                    <div className="mt-6 pt-6 border-t border-white/20">
                        <p className="text-xs text-indigo-200 text-center">
                            🔒 This is a secure admin portal. All login attempts are logged and monitored.
                        </p>
                    </div>
                </div>

                <div className="text-center mt-6">
                    <a href="/" className="text-indigo-200 hover:text-white text-sm transition">
                        ← Back to main site
                    </a>
                </div>
            </div>
        </div>
    );
};

export default AdminLoginPage;